// DrawerCreateLocationForm — slim「新增地點」form designed to fit
// inside AiConsoleDrawer's 240..720px column. Drives the same
// /v1/locations POST as the Locations page's LocationForm but
// without the edit-mode chrome (NFC binding card, task assignments
// list, retire button) — drawer scope is create-only.
//
// Sites list is auto-fetched on mount; while loading the operator sees
// a spinner. If no sites exist the form blocks save and explains the
// dependency in zh-TW so the operator (or AI in subsequent turn) knows
// to create a site first.
//
// AM-7 spec docs/specs/ai-mode-operations-console.md §5.2.

import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { listSites, type SiteRow } from "../lib/sitesApi.js";
import { createLocation, friendlyLocationsError } from "../lib/locationsApi.js";

type LocationRisk = "low" | "medium" | "high";

interface Props {
  prefill: Record<string, unknown>;
  tenantId: string | null;
  onSaved: (entityId: string) => void;
  onCancel: () => void;
}

function readStringPrefill(prefill: Record<string, unknown>, key: string): string {
  const v = prefill[key];
  return typeof v === "string" ? v : "";
}

function readRiskPrefill(prefill: Record<string, unknown>): LocationRisk {
  const v = prefill.risk_level;
  return v === "low" || v === "medium" || v === "high" ? v : "low";
}

export default function DrawerCreateLocationForm({
  prefill, tenantId, onSaved, onCancel,
}: Props): React.ReactElement {
  const [sites, setSites] = useState<SiteRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [siteId, setSiteId] = useState(readStringPrefill(prefill, "site_id"));
  const [code, setCode] = useState(readStringPrefill(prefill, "code"));
  const [label, setLabel] = useState(readStringPrefill(prefill, "label"));
  const [hint, setHint] = useState(readStringPrefill(prefill, "location_hint"));
  const [risk, setRisk] = useState<LocationRisk>(readRiskPrefill(prefill));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await listSites({ tenantId: tenantId ?? undefined, limit: 200 });
        if (cancelled) return;
        setSites(r.items);
        // Auto-pick first site if AI didn't prefill one and only one exists
        // OR prefilled site_id doesn't match anything we got back (stale).
        if (!siteId && r.items.length > 0) setSiteId(r.items[0].id);
        else if (siteId && !r.items.find((s) => s.id === siteId) && r.items.length > 0) {
          setSiteId(r.items[0].id);
        }
      } catch (e) {
        if (cancelled) return;
        setLoadErr((e as Error).message || "案場列表載入失敗");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await createLocation({
        site_id: siteId,
        code: code.trim(),
        label: label.trim(),
        location_hint: hint.trim() || null,
        risk_level: risk,
        status: "active",
      }, tenantId);
      onSaved(r.id);
    } catch (e) {
      setErr(friendlyLocationsError(e));
    } finally {
      setBusy(false);
    }
  };

  if (sites === null && !loadErr) {
    return (
      <div className="flex items-center gap-2 text-text-secondary text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> 載入案場列表中…
      </div>
    );
  }

  if (loadErr) {
    return (
      <div className="space-y-3">
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {loadErr}
        </div>
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">關閉</button>
      </div>
    );
  }

  if (sites && sites.length === 0) {
    return (
      <div className="space-y-3">
        <div className="rounded border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
          這個帳戶還沒有任何案場。請先建立案場後，再回來新增地點。
        </div>
        <button type="button" onClick={onCancel} className="btn-secondary text-sm">關閉</button>
      </div>
    );
  }

  const canSave = !!siteId && code.trim().length > 0 && label.trim().length > 0 && !busy;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">所屬案場 *</label>
        <select className="select w-full" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          {sites!.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">代碼 *</label>
        <input
          className="input font-mono w-full"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="CP-2F-04"
        />
        <p className="mt-1 text-2xs text-text-muted">同一案場內需唯一</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">顯示名稱 *</label>
        <input
          className="input w-full"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="機房後門"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">位置補充</label>
        <input
          className="input w-full"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder="冷氣機左側 1.2m 處"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">風險等級</label>
        <select className="select w-full" value={risk} onChange={(e) => setRisk(e.target.value as LocationRisk)}>
          <option value="low">低</option>
          <option value="medium">中</option>
          <option value="high">高</option>
        </select>
      </div>
      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {err}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary text-sm">取消</button>
        <button type="button" onClick={() => void save()} disabled={!canSave} className="btn-primary text-sm">
          {busy ? "儲存中…" : "新增"}
        </button>
      </div>
    </div>
  );
}
